import React, { useEffect, useState } from 'react';

import { getMyInvoices, getPayoutDetails } from '../../api_handlers/invoice';
import { getInfluencerProfile } from '../../api_handlers/influencer';
import { buildChecklist, checklistProgress, nextStep } from '../invoices/lib/accountChecklist';
import { withReturnTo } from '../invoices/lib/returnTo';

/**
 * "Finish setting up your account" for creators.
 *
 * A creator can see their campaigns straight away, but cannot be paid until a
 * platform is linked and their financial details are on file — both of which
 * live on settings screens they would have to go hunting for. This puts the
 * remaining steps in front of them, in order, with the next one highlighted.
 *
 * Layout note: every row is the same three-column grid (tick, text, action),
 * so the ticks, the headings and the buttons each line up down the card
 * instead of drifting with the length of the text. On narrow screens the
 * action drops to its own row but stays in the text column, keeping the two
 * left edges the list is read against.
 *
 * It hides itself once everything is done, so it does not become furniture.
 */

const CSS = `
/* Scoped reset. The whole app sits inside CRA's .App { text-align: center },
   and this flow's font is Inter, not the Lato the rest of the page declares —
   so the card states both rather than inheriting whatever reaches it. */
.ac-card {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  text-align: left;
  font-family: 'Inter', 'Segoe UI', Roboto, system-ui, sans-serif;
  border: 1px solid var(--inf-line, #e9eaf0);
  border-radius: 16px;
  background: #fff;
  margin: 20px 0 0;
  overflow: hidden;
}
.ac-card *, .ac-card *::before, .ac-card *::after { box-sizing: inherit; }
.ac-card p, .ac-card h2, .ac-card ol, .ac-card li { text-align: left; }

.ac-head { padding: 18px 20px 0; }
.ac-head__row { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.ac-title { font-size: 15px; font-weight: 700; color: var(--inf-ink, #1f2433); margin: 0; line-height: 1.35; }
.ac-count { font-size: 12px; font-weight: 600; color: var(--inf-muted, #6b7280); white-space: nowrap; }
.ac-sub { font-size: 13px; line-height: 1.6; color: var(--inf-muted, #6b7280); margin: 4px 0 0; max-width: 62ch; }
.ac-bar { height: 5px; border-radius: 999px; background: #eef1f7; margin: 14px 0 0; overflow: hidden; }
.ac-bar__fill { height: 100%; border-radius: 999px; background: var(--inf-navy, #111936); transition: width 240ms ease; }

.ac-steps { list-style: none; margin: 16px 0 0; padding: 0; }
.ac-step {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr) auto;
  align-items: start;
  column-gap: 12px;
  row-gap: 12px;
  padding: 14px 20px;
  border-top: 1px solid var(--inf-line, #e9eaf0);
}
.ac-step--next { background: #f8f9fc; }

.ac-tick {
  grid-column: 1; grid-row: 1;
  width: 22px; height: 22px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; line-height: 1;
}
.ac-tick--done { background: #ecfdf3; color: #027a48; border: 1px solid #a6f4c5; }
.ac-tick--todo { background: #fff; color: var(--inf-muted, #6b7280); border: 1px solid #d0d5dd; }
.ac-tick--next { border-color: var(--inf-navy, #111936); }

.ac-body { grid-column: 2; grid-row: 1; min-width: 0; }
.ac-label { font-size: 13.5px; font-weight: 700; color: var(--inf-ink, #1f2433); margin: 0; line-height: 22px; overflow-wrap: anywhere; }
.ac-label--done { color: var(--inf-muted, #6b7280); }
.ac-detail { font-size: 12.5px; line-height: 1.6; color: var(--inf-muted, #6b7280); margin: 3px 0 0; max-width: 58ch; overflow-wrap: anywhere; }

.ac-card .ac-cta {
  grid-column: 3; grid-row: 1;
  display: inline-flex; align-items: center; justify-content: center;
  min-height: 36px; padding: 0 16px;
  border-radius: 8px; border: 1px solid var(--inf-navy, #111936);
  font-family: inherit; font-size: 12.5px; font-weight: 700;
  background: #fff; text-decoration: none; white-space: nowrap; cursor: pointer;
  transition: background-color 180ms ease, color 180ms ease, border-color 180ms ease;
}
.ac-card .ac-cta--primary { background: var(--inf-navy, #111936); border-color: var(--inf-navy, #111936); }
/* The label carries its own colour. An anchor's colour is a popular thing for
   a global stylesheet to claim, and navy-on-navy reads as an empty box. */
.ac-card .ac-cta__label { color: var(--inf-navy, #111936); -webkit-text-fill-color: var(--inf-navy, #111936); }
.ac-card .ac-cta--primary .ac-cta__label { color: #fff; -webkit-text-fill-color: #fff; }
.ac-card .ac-cta:hover { background: #f3f4f8; }
.ac-card .ac-cta--primary:hover { background: var(--inf-navy-700, #1b2550); }
.ac-card .ac-cta:focus-visible { outline: 2px solid var(--inf-navy, #111936); outline-offset: 2px; }

/* Wide: the steps lay out across the card as a stepper, so a full-width
   banner uses the width instead of leaving a long empty middle. Each tile is
   self-contained — tick and label on one line, then the reason, then the
   action pinned to the bottom so the buttons line up across the row. */
@media (min-width: 1080px) {
  .ac-card { margin-top: 0; margin-bottom: 20px; }
  .ac-steps {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin-top: 0;
    border-top: 1px solid var(--inf-line, #e9eaf0);
  }
  .ac-step {
    grid-template-columns: 22px minmax(0, 1fr);
    align-content: start;
    row-gap: 8px;
    border-top: 0;
    border-left: 1px solid var(--inf-line, #e9eaf0);
    padding: 16px 18px;
  }
  .ac-step:first-child { border-left: 0; }
  .ac-detail { max-width: none; }
  .ac-card .ac-cta {
    grid-column: 2; grid-row: 2;
    justify-self: start; align-self: end;
    margin-top: 2px;
  }
}

@media (max-width: 599px) {
  .ac-card { border-radius: 14px; margin-top: 16px; }
  .ac-head { padding: 16px 16px 0; }
  .ac-head__row { flex-direction: column; align-items: flex-start; gap: 2px; }
  .ac-step { grid-template-columns: 22px minmax(0, 1fr); padding: 14px 16px; }
  /* The action drops below the text but keeps its left edge, so the card still
     reads down two columns rather than three ragged ones. */
  .ac-card .ac-cta { grid-column: 2; grid-row: 2; justify-self: start; min-height: 44px; padding: 0 20px; }
}

.ac-sk { border-radius: 6px; background: linear-gradient(90deg, #f1f3f7 25%, #e7eaf1 37%, #f1f3f7 63%); background-size: 400% 100%; animation: ac-shimmer 1.4s ease infinite; }
.ac-sk--title { width: 220px; height: 15px; }
.ac-sk--sub { width: min(100%, 320px); height: 12px; margin-top: 8px; }
.ac-sk--label { width: 180px; height: 13px; }
.ac-sk--detail { width: min(100%, 280px); height: 11px; margin-top: 7px; }
@keyframes ac-shimmer { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }

@media (prefers-reduced-motion: reduce) {
  .ac-sk { animation: none; }
  .ac-bar__fill, .ac-card .ac-cta { transition: none; }
}
`;

function useStyle() {
  useEffect(() => {
    const id = 'account-checklist-css';
    if (document.getElementById(id)) return;
    const tag = document.createElement('style');
    tag.id = id;
    tag.innerHTML = CSS;
    document.head.appendChild(tag);
  }, []);
}

export default function AccountChecklist() {
  useStyle();
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    // Each call is independent: a creator with no invoices yet still needs the
    // rest of the list, so one failure must not blank the whole card.
    Promise.all([
      getInfluencerProfile().catch(() => ({})),
      getPayoutDetails().catch(() => null),
      getMyInvoices().catch(() => []),
    ]).then(([profile, payout, invoices]) => {
      if (!alive) return;
      setData({
        profile: profile || {},
        payout,
        invoices: Array.isArray(invoices) ? invoices : (invoices || {}).results || [],
      });
    });
    return () => {
      alive = false;
    };
  }, []);

  // A skeleton rather than nothing, so the campaign list below does not jump
  // down the page when the card arrives.
  if (!data) {
    return (
      <section className="ac-card ac-card--loading" aria-hidden="true">
        <div className="ac-head">
          <div className="ac-sk ac-sk--title" />
          <div className="ac-sk ac-sk--sub" />
          <div className="ac-bar" />
        </div>
        <ol className="ac-steps">
          {[0, 1, 2].map((row) => (
            <li className="ac-step" key={row}>
              <span className="ac-tick ac-tick--todo" />
              <div className="ac-body">
                <div className="ac-sk ac-sk--label" />
                <div className="ac-sk ac-sk--detail" />
              </div>
            </li>
          ))}
        </ol>
      </section>
    );
  }

  const steps = buildChecklist(data);
  const progress = checklistProgress(steps);
  if (progress.complete) return null;

  const next = nextStep(steps);
  // Settings pages send the creator back here when they are done.
  const here = typeof window === 'undefined' ? '' : window.location.pathname;

  return (
    <section className="ac-card" aria-label="Account setup">
      <div className="ac-head">
        <div className="ac-head__row">
          <h2 className="ac-title">Finish setting up your account</h2>
          <span className="ac-count">
            {progress.done} of {progress.total} done
          </span>
        </div>
        <p className="ac-sub">
          {next && next.id === 'payout'
            ? 'Add your financial details so brands can pay you for completed campaigns.'
            : 'A couple of steps left before you can invoice and get paid.'}
        </p>
        <div
          className="ac-bar"
          role="progressbar"
          aria-valuenow={progress.done}
          aria-valuemin={0}
          aria-valuemax={progress.total}
        >
          <div className="ac-bar__fill" style={{ width: `${progress.percent}%` }} />
        </div>
      </div>

      <ol className="ac-steps">
        {steps.map((step) => {
          const isNext = Boolean(next && step.id === next.id);
          return (
            <li key={step.id} className={`ac-step${isNext ? ' ac-step--next' : ''}`}>
              <span
                className={`ac-tick ${step.done ? 'ac-tick--done' : 'ac-tick--todo'}${
                  isNext ? ' ac-tick--next' : ''
                }`}
                aria-hidden="true"
              >
                {step.done ? '✓' : ''}
              </span>
              <div className="ac-body">
                <p className={`ac-label${step.done ? ' ac-label--done' : ''}`}>{step.label}</p>
                <p className="ac-detail">{step.detail}</p>
              </div>
              {!step.done && step.href ? (
                <a
                  className={`ac-cta${isNext ? ' ac-cta--primary' : ''}`}
                  href={withReturnTo(step.href, here)}
                >
                  <span className="ac-cta__label">{step.cta}</span>
                </a>
              ) : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
